import { ButtonGroup, Card, IconButton, LuIcon, stopPropagation, Text } from "@overmap-ai/blocks"
import type { Asset, AssetType, AssetTypeStatus, Stored } from "@overmap-ai/common"
import { cx } from "class-variance-authority"
import { type ComponentProps, memo, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { AssetTypeDropdownMenu } from "../DropdownMenus/AssetTypeDropdownMenu"
import { AssetStatusPieChart } from "../Graphs/AssetStatusPieChart"
import { AssetTypeIcon } from "../Icons/AssetTypeIcon"

interface AssetTypeCardProps extends Omit<ComponentProps<typeof Card>, "children" | "onClick"> {
	assetType: Stored<AssetType>
	assets: Stored<Asset>[]
	assetTypeStatuses: Stored<AssetTypeStatus>[]
	onClick?: (assetType: Stored<AssetType>) => void
}

export const AssetTypeCard = memo<AssetTypeCardProps>((props) => {
	const { className, assetType, assets, assetTypeStatuses, onClick, ...rest } = props

	const handleClick = useCallback(() => {
		onClick?.(assetType)
	}, [assetType, onClick])

	return (
		<Card className={cx("flex flex-col gap-2", className)} onClick={handleClick} {...rest}>
			<div className="flex w-full items-center gap-2">
				<div className="flex min-w-0 items-center gap-2">
					<AssetTypeIcon assetTypeId={assetType.uuid} />
					<Text className="truncate">{assetType.name}</Text>
					{assets.length > 0 ? (
						<Text size="sm" accentColor="base" weight="regular">
							{assets.length}
						</Text>
					) : null}
				</div>
				<ButtonGroup
					className="flex grow items-center justify-end gap-2"
					accentColor="base"
					variant="ghost"
					size="sm"
				>
					<AssetTypeDropdownMenu modal assetType={assetType} size="sm">
						<IconButton aria-label="more" onClick={stopPropagation}>
							<LuIcon icon={modelIconMapping.more} />
						</IconButton>
					</AssetTypeDropdownMenu>
				</ButtonGroup>
			</div>
			<div className="flex size-full items-center justify-center">
				<AssetStatusPieChart
					className="pointer-events-none"
					assets={assets}
					assetTypeStatuses={assetTypeStatuses}
					width="100%"
					height="100%"
					hideTooltip
				/>
			</div>
		</Card>
	)
})
AssetTypeCard.displayName = "AssetTypeCard"
